import { GameWeek, SeasonID } from 'types/index';

export enum Stores {
  /**
   * `GameSchedule` stores the NFL regular season schedule without any stats or scores
   *
   *  @note
   * `keyPath: gameID` where gameID is in the format of "20240905_BAL@KC"
   *
   * `indexes`: `['weekSeason', ['gameWeek', 'season']`,
   *            `['seasonWeekID']`, `['gameWeek']`, `['season']`,
   *
   * @abstract
   * This model is used to
   * - show the schedule of games for a given week
   * - show the schedule of games for a given team
   * - show the schedule of games for a given season
   * - determine the bye week for every team
   * - could be loaded before the games are played
   *
   * @example
   * away: "BAL",
   * gameDate: "20240905"
   * gameID: "20240905_BAL@KC"
   * gameStatus: "Final"
   * gameStatusCode: "2"
   * gameTime: "8:40p"
   * gameTime_epoch: "1725583200.0"
   * gameWeek: "Week 1"
   * home: "KC"
   * neutralSite: "False"
   * season: "2024"
   * seasonType: "Regular Season"
   * teamIDAway: "3"
   * teamIDHome: "16"
   */
  GameSchedule = 'gameSchedule',

  /**
   * `GameStats` stores the NFL game stats from every completed NFL game during the regular season
   *
   * @note
   * `keyPath: gameID` where gameID is in the format of "20240905_BAL@KC"
   *
   * `indexes`: `['seasonWeekID']`
   *
   * @abstract
   * This model is used to
   * - show the score of each game
   * - show the stats of each game
   * - derive stats for each team
   */
  GameStats = 'gameStats',

  /**
   * `TeamInfo` stores the NFL team information
   *  @note
   * `keyPath: teamID` where `teamID ` is in the format of "0001, 0002...0032"
   *  where:
   *   - 2024 = season
   *   - 01 = weekID (01-18)
   *   - 01 = teamID (01-32)
   *   - Week1 = gameWeek
   *   - 1 = teamID as it appears in RapidAPI (1-32)
   *   - ARI = team abbreviation
   *   - 20240908_ARI@BUF = gameID
   *
   * `indexes`: `none`
   *
   * @abstract
   * This model is used to
   * - show editable general information about each team
   */
  TeamInfo = 'teamInfo',

  /**
   * `TeamStats` stores each NFL team stats for each week of the season
   *
   * @note
   * `keyPath: uID` where uID is in the format of "2024-01-01-Week1-1-ARI-20240908_ARI@BUF"
   *  where:
   *   - 2024 = season
   *   - 01 = weekID (01-18)
   *   - 01 = teamID (01-32)
   *   - Week1 = gameWeek
   *   - 1 = teamID as it appears in RapidAPI (1-32)
   *   - ARI = team abbreviation
   *   - 20240908_ARI@BUF = gameID
   *
   * `indexes`: `seasonWeekID`
   *
   * This model is used to
   * - show the stats of each team for each week
   * - derive the stats for each team for the season
   * - derive the rankings of each team for the season
   */
  TeamWeeklyStats = 'teamWeeklyStats',

  /**
   * `Predictions` stores the game predictions for each game
   *
   * @note
   * `keyPath: ['gameID', 'modelVersion']`
   *
   * `indexes`: `['seasonWeekID']`, `['modelVersion']`
   *
   * @abstract
   * This model is used to
   * - show the predictions for each game
   * - compare predictions between models
   */
  Predictions = 'predictions',

  /**
   * `TeamsOnByeWeek` stores the list of teams on bye week for each week of the season
   *
   * @note
   * `keyPath: seasonWeekID` where `seasonWeekID` is in the format of "2024-01"
   *  where:
   *   - 2024 = season
   *   - 01 = weekNum (01-18)
   *
   * `indexes`: `none`
   */
  TeamsOnByeWeek = 'teamsOnByeWeek',

  /**
   * `WeeklyRanks` stores the weekly rankings for each team for each week of the season
   *
   * @note
   * `keyPath: uID` where uID is in the format of "2024-01-01-Week1-1-ARI-20240908_ARI@BUF"
   *  where:
   *   - 2024 = season
   *   - 01 = weekID (01-18)
   *   - 01 = teamID (01-32)
   *   - Week1 = gameWeek
   *   - 1 = teamID as it appears in RapidAPI (1-32)
   *   - ARI = team abbreviation
   *   - 20240908_ARI@BUF = gameID
   *
   * `indexes`: `none`
   */
  WeeklyRanks = 'weeklyRanks',

  /**
   * `SyncStatus` stores the status of the last time the data was synced from the API
   */
  SyncStatus = 'syncStatus',

  /**
   * `SeasonStandings` stores the weekly standings for each team for the season
   *
   *  @note
   * `keyPath: uID` where uID is in the format of "2024-01-01-Week1-1-ARI-20240908_ARI@BUF"
   *  where:
   *   - 2024 = season
   *   - 01 = weekID (01-18)
   *   - 01 = teamID (01-32)
   *   - Week1 = gameWeek
   *   - 1 = teamID as it appears in RapidAPI (1-32)
   *   - ARI = team abbreviation
   *   - 20240908_ARI@BUF = gameID
   *
   * `indexes`: `['seasonWeekID']`
   *
   * @example
   * awayRecord: "0-1-0"
   * homeRecord: "0-0-0"
   * inDivisionAwayRecord: "0-0-0"
   * inDivisionHomeRecord: "0-0-0"
   * isByeWeek: false
   * isHomeTeam: false
   * losses: 1
   * pa: 34
   * pct: 0
   * pf: 28
   * pointsAllowed: 34
   * pointsScored: 28
   * result: "L"
   * seasonWeekID: "2024-01"
   * streak: -1
   * ties: 0
   * uID: "2024-01-01-Week1-1-ARI-20240908_ARI@BUF"
   * wins: 0
   */
  SeasonStandings = 'seasonStandings',

  /**
   * `CategoryRankings` stores the detailed rankings for each category for a specific week
   *
   * @note
   * `keyPath: ['seasonWeekID', 'category']`
   *
   * `indexes`: `['seasonWeekID']`, `['category']`
   *
   * @abstract
   * This model is used to
   * - show detailed rankings for a specific category
   * - compare teams across specific categories
   */
  CategoryRankings = 'categoryRankings',

  /**
   * `TeamSeasonStats` stores weekly breakdown of stats for each team for the season
   *
   * @note
   * `keyPath: seasonTeamAbrvID` where `seasonTeamAbrvID` is in the format of "2025-01-ARI"
   *  where:
   *   - 2025 = season year
   *   - 01 = zero-padded numerical team ID (01-32)
   *   - ARI = team abbreviation
   *
   * `indexes`: `['teamAbrvID']`, `['seasonID']`
   *
   * @abstract
   * This model is used to
   * - show weekly statistical breakdown for each team
   * - analyze team performance week-by-week
   */
  TeamSeasonStats = 'teamSeasonStats',

  /**
   * `TeamCumulativeStats` stores season-to-date cumulative stats for each team
   *
   * @note
   * `keyPath: seasonTeamAbrvID` where `seasonTeamAbrvID` is in the format of "2025-01-ARI"
   *  where:
   *   - 2025 = season year
   *   - 01 = zero-padded numerical team ID (01-32)
   *   - ARI = team abbreviation
   *
   * `indexes`: `['teamAbrvID']`, `['seasonID']`
   *
   * @abstract
   * This model is used to
   * - show cumulative season statistics for each team
   * - compare overall team performance across the season
   */
  TeamCumulativeStats = 'teamCumulativeStats',

  /**
   * `TeamSeasonRanks` stores rank values (1-32) for each category for each team
   *
   * @note
   * `keyPath: seasonTeamAbrvID` where `seasonTeamAbrvID` is in the format of "2025-01-ARI"
   *  where:
   *   - 2025 = season year
   *   - 01 = zero-padded numerical team ID (01-32)
   *   - ARI = team abbreviation
   *
   * `indexes`: `['teamAbrvID']`, `['seasonID']`
   *
   * @abstract
   * This model is used to
   * - show rank position for each statistical category
   * - provide quick team strength comparisons
   */
  TeamSeasonRanks = 'teamSeasonRanks',

  /**
   * `HeadToHead` stores all-time head-to-head matchup records between teams
   *
   * @note
   * `keyPath: headToHeadID` where `headToHeadID` is in the format of "01-ARI_02-ATL"
   *  where:
   *   - 01-ARI = first team (numerically lower TeamAbrvID)
   *   - 02-ATL = second team (numerically higher TeamAbrvID)
   *
   * `indexes`: `none`
   *
   * @abstract
   * This model is used to
   * - show historical matchup records between any two teams
   * - display win/loss/tie counts across all seasons
   * - provide game history for each matchup
   * - 496 total records (32 teams × 31 opponents ÷ 2)
   *
   * @example
   * headToHeadID: "01-ARI_02-ATL"
   * team1AbrvID: "01-ARI"
   * team2AbrvID: "02-ATL"
   * allTimeRecord: { team1Wins: 18, team2Wins: 20, ties: 0 }
   * games: [{ gameID: "20240908_ARI@ATL", winner: "02-ATL", ... }]
   */
  HeadToHead = 'headtohead',
}

// #region WEB WORKER RELATED TYPES/INTERFACES ********************************

export interface IIndexDbInfo {
  idbName: string;
  idbVersion: number;
}

export interface IStoreIndexDb extends IIndexDbInfo {
  storeName: string;
}

export type WorkerTaskType = 'stores-count' | 'stores-names' | 'stores-records-count' | 'store-data';
export type RequestWorkerTask = `get-${WorkerTaskType}`;
export type ResultWorkerTask = `result-${RequestWorkerTask}`;
export type ActiveWorkerTask = WorkerTaskType | RequestWorkerTask | ResultWorkerTask;

export interface IWorkerIDBAuditMessage {
  request: IIndexDbInfo;
  type: ActiveWorkerTask;
  payload: any;
}

export interface IWorkerIDBLoaderRequestMessage {
  request: IStoreIndexDb;
  type: ActiveWorkerTask;
  payload: any;
}

export interface IWorkerIDBLoaderResponseMessage {
  type: ActiveWorkerTask | 'error-get-store-data';
  payload: any;
}
// #endregion

export type IDBLoaderFunction = {
  storeName: Stores;
  callbackFn: (data: any) => void;
  year: SeasonID | 'full';
  shouldFetch: boolean;
  weekID?: GameWeek;
  modelVersion?: string;
};

export type SyncIDBStores = {
  [key in Stores]: SyncStoreProps;
};

export type SyncStoreProps = {
  id: Stores;
  lastUpdatedOn: number;
  timestamp: string;
};

export type SyncIDBPayload = {
  syncStores: SyncIDBStores;
  feLastUpdatedOn: number;
  clientLastUpdatedOn: string;
};
